<script lang="ts" setup>
  import { FunctionalComponent, onMounted, ref, Ref, SVGAttributes } from 'vue'
  import DataTable from 'primevue/datatable'
  import { useI18n } from 'vue-i18n'
  import { useFeatureFlags } from '@/composables/useFeatureFlag.ts'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import AddNewFeatureFlagDialog from '@/components/titrop-settings/AddNewFeatureFlagDialog.vue'
  import { EffortEntryJsonldDefaultRead, FeatureFlagJsonldDefaultRead } from '@/api'
  import type ContextMenu from 'primevue/contextmenu'
  import { DeleteIcon, EditIcon, ExclamationTriangleIcon } from '@/config/icons.ts'
  import { MenuItem } from 'primevue/menuitem'
  import EditNotesDialog from '@/components/titrop-settings/EditNotesDialog.vue'
  import { useConfirm } from 'primevue/useconfirm'
  import ConfirmDialog from 'primevue/confirmdialog'
  import DropdownButton from '@/elements/DropdownButton.vue'

  const featureFlags = useFeatureFlags()
  const { t } = useI18n()
  const toastService = useToastSimplified()
  const confirm = useConfirm()

  const isLoading = ref<boolean>(true)

  const isAddFFDialogVisible = ref<boolean>(false)
  const isEditNotesDialogVisible = ref<boolean>(false)

  const selectedItem = ref<EffortEntryJsonldDefaultRead>()
  const contextMenuEntries = ref<MenuItem[]>()
  const cm = ref<InstanceType<typeof ContextMenu>>() as Ref<InstanceType<typeof ContextMenu>>
  const flagToEdit = ref<FeatureFlagJsonldDefaultRead>()
  const flagToDelete = ref<FeatureFlagJsonldDefaultRead>()

  onMounted(async () => {
    isLoading.value = false
  })

  const onEnableChange = async (id: string, enabled: boolean) => {
    const res = await featureFlags.changeEnable(id, enabled)
    toastService.add({
      severity: 'success',
      life: 2500,
      summary: res.enabled ? 'Feature Flag ist aktiviert' : 'Feature Flag ist deaktiviert'
    })
  }

  const onAddFFClick = () => {
    isAddFFDialogVisible.value = true
  }

  const onAddFFSubmit = async (/*flag: { enabled: boolean, location: 'backend' | 'frontend' | 'shared', name: string, notes?: string }*/) => {
    toastService.addPredefined('addConfirmation')
  }

  const onTableContextMenu = (event: MouseEvent) => {
    if (!selectedItem.value) return
    if (event.ctrlKey) {
      cm.value.hide()
      selectedItem.value = undefined
      return
    }
    contextMenuEntries.value = generateContextMenu(event)
    event.preventDefault()
    cm.value.show(event)
  }

  const generateContextMenu = (event: MouseEvent): MenuItem[] => {
    if (!selectedItem.value) {
      return []
    }
    let contextItems!: (Omit<MenuItem, 'icon'> & { icon?: FunctionalComponent<SVGAttributes> })[]

    contextItems = [
      {
        label: 'Notes bearbeiten',
        icon: EditIcon,
        command: () => {
          onEditNotesClick(selectedItem.value!)
        }
      },
      {
        label: 'Löschen',
        icon: DeleteIcon,
        command: async () => {
          flagToDelete.value = selectedItem.value
          await confirm1(event)
        }
      }
    ]

    return contextItems as MenuItem[]
  }

  const onContextMenuHide = () => {
    selectedItem.value = undefined
  }

  const onEditNotesClick = (flag: FeatureFlagJsonldDefaultRead) => {
    flagToEdit.value = flag
    isEditNotesDialogVisible.value = true
  }

  const onEditNotesSubmit = async (values: { notes: string, id: string }) => {
    await featureFlags.changeNotes(values.id, values.notes)
    isEditNotesDialogVisible.value = false
    toastService.addPredefined('saveConfirmation')
    flagToEdit.value = undefined
  }

  const confirm1 = async (event: MouseEvent) => {
    confirm.require({
      target: event.currentTarget as HTMLElement,
      group: 'deleteFlagConfirm',
      message: 'Der Eintrag wird unwiderruflich gelöscht. Fortfahren?',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Abrechen',
      acceptLabel: 'Löschen',
      accept: async () => {
        if (!flagToDelete.value) return
        await featureFlags.deleteFlag(flagToDelete.value.id!)
        toastService.addPredefined('deleteConfirmation')
      },
      reject: () => {
        flagToDelete.value = undefined
      }
    })
  }

  const onDropdownButtonClick = (flag: FeatureFlagJsonldDefaultRead) => {
    selectedItem.value = flag
  }

  const dropdownButtonOptions = ref([
    {
      label: 'Notes bearbeiten',
      icon: 'EditIcon',
      value: () => {
        onEditNotesClick(selectedItem.value!)
      }
    },
    {
      label: 'Löschen',
      icon: 'DeleteIcon',
      value: () => {
        flagToDelete.value = selectedItem.value!
        confirm1(new MouseEvent('fakeEvent'))
      }
    }
  ])
</script>

<template>
  <DataTable
    class="hidden md:block text-sm"
    :value="featureFlags.allFeatureFlags.value"
    :loading="isLoading"
    v-model:context-menu-selection="selectedItem"
    stripedRows
    showGridlines
    size="small"
    column-resize-mode="fit"
    resizable-columns
    @contextmenu="onTableContextMenu">
    <template #header>
      <div class="flex justify-between">
        <div class="flex items-center">
          <ButtonGroup>
            <ButtonDefault outlined icon="PlusIcon" size="small" @click="onAddFFClick" />
          </ButtonGroup>
        </div>
        <div class="text-right flex gap-x-2">
        </div>
      </div>
    </template>

    <Column header="ID" field="id"
            :style="{ width: '70px' }" />

    <Column
      header="Name"
      field="name"
      :style="{ width: '200px' }" />

    <Column header="Location"
            field="location"
            :style="{ width: '90px' }">
      <template #body="data">
        {{ t(data.data.location) }}
      </template>
    </Column>

    <Column header="Notes"
            field="notes"
            style="width: 200px; max-width: 900px;">
      <template #body="data">
        <div
          class="w-full h-full overflow-hidden break-words"
          style="white-space: normal;"
          v-titrop-html="data.data.notes"
          @dblclick="onEditNotesClick(data.data)" />
      </template>
    </Column>

    <Column header="Enabled" field="enabled" :style="{ width: '90px' }">
      <template #body="data">
        <ToggleButton
          v-model="data.data.enabled"
          :onLabel="t('on')"
          :offLabel="t('off')"
          size="small"
          class="w-full"
          @change="onEnableChange(data.data.id, data.data.enabled)" />
      </template>
    </Column>

    <Column header="Erstellungsdatum" field="createdAt" :style="{ width: '90px' }">
      <template #body="data">
        {{ $formatter.Date(data.data.createdAt) }}
      </template>
    </Column>
  </DataTable>

  <div class="block md:hidden">
    <div class="mb-2">
      <Toolbar>
        <template #start>
          <ButtonGroup>
            <ButtonDefault outlined icon="PlusIcon" size="small" @click="onAddFFClick" />
          </ButtonGroup>
        </template>
      </Toolbar>
    </div>
    <div class="grid grid-cols-1 gap-2 w-full md:p-0 items-start">
      <div
        v-for="flag in featureFlags.allFeatureFlags.value"
        :key="flag.id"
        class="titrop-panel rounded-lg shadow-sm p-4 hover:shadow-lg hover:border-gray-300 border border-gray-200 w-full flex flex-col h-auto">
        <div class="text-lg font-bold border-b pb-1 flex items-center w-full justify-between">
          <div class="flex items-center">
            <span v-titrop-html="flag.name" />
          </div>
          <div>
            <DropdownButton
              icon="MoreIcon"
              size="small"
              severity="secondary"
              :values="dropdownButtonOptions"
              @buttonClick="onDropdownButtonClick(flag as FeatureFlagJsonldDefaultRead)" />
          </div>
        </div>

        <div class="max-w-full break-words overflow-hidden">
          <p class="text-gray-600 pt-1" v-titrop-html="flag.notes" />
        </div>

        <div class="flex justify-between items-center mt-2 text-sm text-gray-500 mb-1">
          <span>
            {{ t(flag.location as string) }}
          </span>
          <span>
            {{ $formatter.DateTime(flag.createdAt) }}
          </span>
        </div>

        <div class="mt-auto pt-1 w-full border-t border-gray-200">
          <div class="pt-1 flex justify-between items-center w-full">
            <ToggleButton
              v-model="flag.enabled"
              :onLabel="t('on')"
              :offLabel="t('off')"
              size="small"
              class="w-full"
              @change="onEnableChange(String(flag.id),flag.enabled!)" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <ConfirmDialog group="deleteFlagConfirm">
    <template #container="{ message, acceptCallback, rejectCallback }:
    { message: ConfirmationOptions_CopyFromPv; acceptCallback: () => void; rejectCallback: () => void }">
      <div class="flex flex-col items-center p-5 border rounded-xl">
        <div class="rounded-full bg-white dark:bg-gray-900 inline-flex h-16 w-16 -mt-12">
          <ExclamationTriangleIcon class="h-full w-full fill-red-400 text-red-400" />
        </div>
        <span class="font-semibold text-2xl block mb-2 mt-2">{{ message.header }}</span>
        <p
          v-titrop-html="message.message"
          class="mb-0" />
        <div class="flex items-center justify-between gap-x-2 w-full mt-2">
          <ButtonDefault
            :label="message.rejectLabel"
            outlined
            severity="danger"
            icon="CancelIcon"
            size="small"
            @click="rejectCallback" />
          <ButtonDefault
            :label="message.acceptLabel"
            outlined
            severity="success"
            icon="DeleteIcon"
            size="small"
            @click="acceptCallback" />
        </div>
      </div>
    </template>
  </ConfirmDialog>

  <ContextMenu
    ref="cm"
    :model="contextMenuEntries"
    @hide="onContextMenuHide">
    <template #itemicon="itm">
      <component
        :is="itm.item.icon"
        :class="[itm.class]" />
    </template>
  </ContextMenu>

  <AddNewFeatureFlagDialog
    v-if="isAddFFDialogVisible"
    v-model:is-visible="isAddFFDialogVisible"
    class="w-full md:w-[40%]"
    @submit="onAddFFSubmit" />

  <EditNotesDialog
    v-if="isEditNotesDialogVisible"
    v-model:is-visible="isEditNotesDialogVisible"
    class="w-full md:w-[40%]"
    :flag="flagToEdit!"
    @submit="onEditNotesSubmit" />

</template>

<i18n>
{
  "de": {
    "frontend": "Client",
    "backend": "Server",
    "shared": "Client+Server",
    "on": "Aktiv",
    "off": "Inaktiv"
  }
}
</i18n>
